import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Avatar,
  Pagination as MuiPagination,
  Paper,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Grid,
  CircularProgress,
  Alert,
  Switch,
} from '@mui/material';
import {
  MoveToInbox as MoveIcon,
  Inventory as ContainerIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Refresh as RecodeIcon,
  Edit as EditIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useReclassifyCore } from '../../../../core/modules/reclassify';
import { ArticleForReclassify } from '../../../../core/modules/reclassify/interfaces/get-articles-for-reclassify';
import { useLocationCore } from '../../../../core/modules/location';
import { useContainerCore } from '../../../../core/modules/container';
import { useCompanyCore } from '../../../../core/modules/company';
import { useArticlesCore } from '../../../../core/modules/articles';
import { useMasterCore } from '../../../../core/modules/master';
import { DestinationTypeSelector, DestinationType } from '../../shared/DestinationTypeSelector';
import { useNavigationCore } from '../../../../core/router/navigation';
import Swal from 'sweetalert2';

interface ReclassifyListProps {
  onOpenReclassifyDialog: (articles: ArticleForReclassify[]) => void;
}

export const ReclassifyList: React.FC<ReclassifyListProps> = ({
  onOpenReclassifyDialog,
}) => {
  const {
    articles,
    selectedArticles,
    pagination,
    filters,
    articlesLoading,
    productStates: reclassifyProductStates,
    selectArticle,
    unselectArticle,
    selectAllArticles,
    clearSelection,
    isArticleSelected,
    getSelectedArticlesData,
    loadArticles,
    setFilters,
    loadProductStates,
    recodeProduct,
    recodeProductV2,
  } = useReclassifyCore();

  const { locations, getLocations } = useLocationCore();
  const { containers, getContainers } = useContainerCore();
  const { companyId } = useCompanyCore();
  const { articles: masterArticles, getArticles } = useArticlesCore();
  const { goToArticles } = useNavigationCore();
  const [masterArticlesSearchResults, setMasterArticlesSearchResults] = useState<any[]>([]);
  const [masterArticlesSearchLoading, setMasterArticlesSearchLoading] = useState(false);
  const { 
    productUsages,
    productCompletenesses,
    productFuntionalities,
    productPackagings,
    productStates: masterProductStates,
    productAesthetics,
    getProductAttributes
  } = useMasterCore();

  const [searchCode, setSearchCode] = useState('');
  const [quantitiesToMove, setQuantitiesToMove] = useState<Record<number, number>>({});
  const [articleStatuses, setArticleStatuses] = useState<Record<number, number>>({});
  const [articleObservations, setArticleObservations] = useState<Record<number, string>>({});
  
  // Estados para clasificación detallada de cada artículo
  const [articleClassificationData, setArticleClassificationData] = useState<Record<number, {
    usage_id?: number;
    usage_observation?: string;
    packaging_id?: number;
    packaging_observation?: string;
    functionality_id?: number;
    functionality_observation?: string;
    aesthetic_id?: number;
    aesthetic_observation?: string;
    completeness_id?: number;
    completeness_observation?: string;
    state_id?: number;
    reference?: string;
  }>>({});
  const [recodeDialogOpen, setRecodeDialogOpen] = useState(false);
  const [selectedArticleForRecode, setSelectedArticleForRecode] = useState<ArticleForReclassify | null>(null);
  const [newCode, setNewCode] = useState('');
  const [selectedExistingArticle, setSelectedExistingArticle] = useState<any | null>(null);
  const [recodeDestinationType, setRecodeDestinationType] = useState<DestinationType>('location');
  const [recodeSelectedLocation, setRecodeSelectedLocation] = useState<number | null>(null);
  const [recodeSelectedContainer, setRecodeSelectedContainer] = useState<number | null>(null);
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [recodeMode, setRecodeMode] = useState<'manual' | 'search' | 'auto'>('manual');
  const [codeExists, setCodeExists] = useState<boolean | null>(null);
  const [codeValidationLoading, setCodeValidationLoading] = useState(false);
  const [newCodeAfterRecode, setNewCodeAfterRecode] = useState<string | null>(null);
  
  
  const [recodeLoading, setRecodeLoading] = useState(false);
  const [recodeError, setRecodeError] = useState<string | null>(null);
  
  // Estados para el modal de edición detallada
  const [detailedEditDialogOpen, setDetailedEditDialogOpen] = useState(false);
  const [selectedArticleForDetailedEdit, setSelectedArticleForDetailedEdit] = useState<ArticleForReclassify | null>(null);
  const [detailedEditLoading, setDetailedEditLoading] = useState(false);
  const [detailedEditError, setDetailedEditError] = useState<string | null>(null);
  const [detailedForm, setDetailedForm] = useState({
    usage_id: 0,
    usage_observation: '',
    packaging_id: 0,
    packaging_observation: '',
    functionality_id: 0,
    functionality_observation: '',
    aesthetic_id: 0,
    aesthetic_observation: '',
    completeness_id: 0,
    completeness_observation: '',
    state_id: 0,
    reference: '',
    observation: ''
  });

  useEffect(() => {
    // Solo cargar estados de productos al montar el componente
    loadProductStates();
  }, [loadProductStates]);

  // Cargar opciones de clasificación cuando se monta el componente
  useEffect(() => {
    if (companyId) {
      getProductAttributes(companyId, 'usage');
      getProductAttributes(companyId, 'packaging');
      getProductAttributes(companyId, 'functionality');
      getProductAttributes(companyId, 'aesthetic');
      getProductAttributes(companyId, 'completeness');
      getProductAttributes(companyId, 'state');
    }
  }, [companyId, getProductAttributes]);


  // Ya no necesitamos este useEffect porque ahora buscamos directamente

  useEffect(() => {
    // Cargar ubicaciones y contenedores para el modal de recodificar
    if (companyId) {
      getLocations(companyId);
      getContainers(companyId, {});
    }
  }, [companyId, getLocations, getContainers]);

  // Debug: Log cuando newCode cambia
  useEffect(() => {
    console.log('🔍 newCode changed to:', newCode);
  }, [newCode]);

  // Debug: Log cuando selectedExistingArticle cambia
  useEffect(() => {
    console.log('🔍 selectedExistingArticle changed to:', selectedExistingArticle);
  }, [selectedExistingArticle]);

  // Forzar re-render cuando se selecciona un artículo
  useEffect(() => {
    if (selectedExistingArticle && selectedExistingArticle.code) {
      console.log('🔄 Forcing state update for selected article:', selectedExistingArticle);
      setNewCode(selectedExistingArticle.code);
    }
  }, [selectedExistingArticle]);

  const handleSelectAll = () => {
    if (articles && selectedArticles.length === articles.length) {
      clearSelection();
    } else {
      selectAllArticles();
    }
  };

  const handleArticleSelect = (articleId: number) => {
    if (isArticleSelected(articleId)) {
      unselectArticle(articleId);
    } else {
      selectArticle(articleId);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    const newFilters = { ...filters, page: newPage };
    setFilters(newFilters);
    loadArticles(newFilters);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchCode(value);
    const newFilters = { code: value, page: 1 };
    setFilters(newFilters);
    loadArticles(newFilters);
  };

  const handleReclassifySelected = () => {
    const selectedData = getSelectedArticlesData();
    if (selectedData.length > 0) {
      // Enriquecer los datos con los cambios del usuario
      const enrichedData = selectedData.map(article => ({
        ...article,
        status_id: articleStatuses[article.id] || article.status_id,
        quantity: quantitiesToMove[article.id] || article.quantity,
        observation: articleObservations[article.id] || ''
      }));
      onOpenReclassifyDialog(enrichedData);
    }
  };

  const handleQuantityChange = (articleId: number, quantity: number) => {
    setQuantitiesToMove(prev => ({
      ...prev,
      [articleId]: Math.max(0, Math.min(quantity, articles?.find(a => a.id === articleId)?.quantity || 0))
    }));
  };

  const handleStatusChange = (articleId: number, statusId: number) => {
    setArticleStatuses(prev => ({
      ...prev,
      [articleId]: statusId
    }));
  };

  const handleObservationChange = (articleId: number, observation: string) => {
    setArticleObservations(prev => ({
      ...prev,
      [articleId]: observation
    }));
  };




  const handleDetailedEditClick = (article: ArticleForReclassify) => {
    setSelectedArticleForDetailedEdit(article);
    setDetailedEditError(null);
    
    // Inicializar formulario con datos existentes del artículo
    setDetailedForm({
      usage_id: article.usage_id || 0,
      usage_observation: article.usage_observation || '',
      packaging_id: article.packaging_id || 0,
      packaging_observation: article.packaging_observation || '',
      functionality_id: article.functionality_id || 0,
      functionality_observation: article.functionality_observation || '',
      aesthetic_id: article.aesthetic_id || 0,
      aesthetic_observation: article.aesthetic_observation || '',
      completeness_id: article.completeness_id || 0,
      completeness_observation: article.completeness_observation || '',
      state_id: article.state_id || article.status_id || 0,
      reference: article.reference || '',
      observation: article.observation || ''
    });
    
    setDetailedEditDialogOpen(true);
  };

  const handleSaveDetailedEdit = () => {
    if (!selectedArticleForDetailedEdit) return;
    
    // Guardar los datos de clasificación detallada para este artículo
    setArticleClassificationData(prev => ({
      ...prev,
      [selectedArticleForDetailedEdit.id]: {
        usage_id: detailedForm.usage_id || undefined,
        usage_observation: detailedForm.usage_observation || undefined,
        packaging_id: detailedForm.packaging_id || undefined,
        packaging_observation: detailedForm.packaging_observation || undefined,
        functionality_id: detailedForm.functionality_id || undefined,
        functionality_observation: detailedForm.functionality_observation || undefined,
        aesthetic_id: detailedForm.aesthetic_id || undefined,
        aesthetic_observation: detailedForm.aesthetic_observation || undefined,
        completeness_id: detailedForm.completeness_id || undefined,
        completeness_observation: detailedForm.completeness_observation || undefined,
        state_id: detailedForm.state_id || undefined,
        reference: detailedForm.reference || undefined,
      }
    }));
    
    setDetailedEditDialogOpen(false);
  };

  const handleRecodeClick = (article: ArticleForReclassify) => {
    setSelectedArticleForRecode(article);
    setNewCode('');
    setSelectedExistingArticle(null);
    setRecodeSelectedLocation(null);
    setRecodeSelectedContainer(null);
    setIsSearchMode(false);
    setRecodeMode('manual');
    setCodeExists(null);
    setNewCodeAfterRecode(null);
    setMasterArticlesSearchResults([]);
    
    // Inicializar cantidad para recodificar
    setQuantitiesToMove(prev => ({
      ...prev,
      [article.id]: article.quantity || 0
    }));
    
    setRecodeError(null);
    setRecodeDialogOpen(true);
  };

  const handleSearchExisting = async (query: string) => {
    // Limpiar timeout anterior
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (query.length < 2) {
      setMasterArticlesSearchResults([]);
      setCurrentSearchQuery('');
      return;
    }
    
    setCurrentSearchQuery(query);
    setMasterArticlesSearchLoading(true);
    
    // Debounce de 500ms
    const timeout = setTimeout(async () => {
      if (companyId) {
        try {
          await getArticles(companyId, { code: query, description: query }, () => {
            // onSuccess - los resultados están en masterArticles
          }, () => {
            setMasterArticlesSearchResults([]);
          });
          // Los resultados se actualizarán en el useEffect que observa masterArticles
        } catch (error) {
          console.error('Error searching articles:', error);
          setMasterArticlesSearchResults([]);
        } finally {
          setMasterArticlesSearchLoading(false);
        }
      } else {
        setMasterArticlesSearchLoading(false);
      }
    }, 500);
    
    setSearchTimeout(timeout);
  };

  // Validar si un código existe
  const validateCodeExists = async (code: string) => {
    if (!code.trim() || !companyId) {
      setCodeExists(null);
      return;
    }
    
    setCodeValidationLoading(true);
    try {
      await getArticles(companyId, { code: code.trim() }, () => {
        // Si encuentra artículos, el código existe
        setCodeExists(true);
      }, () => {
        // Si no encuentra, el código no existe
        setCodeExists(false);
      });
    } catch (error) {
      setCodeExists(null);
    } finally {
      setCodeValidationLoading(false);
    }
  };

  // Efecto para actualizar resultados de búsqueda cuando cambian masterArticles
  useEffect(() => {
    if (currentSearchQuery && masterArticles && Array.isArray(masterArticles)) {
      setMasterArticlesSearchResults(masterArticles);
    }
  }, [masterArticles, currentSearchQuery]);

  const handleRecodeDestinationTypeChange = (type: DestinationType) => {
    setRecodeDestinationType(type);
    setRecodeSelectedLocation(null);
    setRecodeSelectedContainer(null);
  };

  const handleRecodeLocationChange = (locationId: number | null) => {
    setRecodeSelectedLocation(locationId);
    setRecodeSelectedContainer(null);
  };

  const handleRecodeContainerChange = (containerId: number | null) => {
    setRecodeSelectedContainer(containerId);
    setRecodeSelectedLocation(null);
  };

  const handleRecodeSubmit = async () => {
    if (!selectedArticleForRecode || !companyId) return;
    
    // Validar según el modo
    if (recodeMode === 'auto') {
      // Modo automático - no necesita código
    } else if (recodeMode === 'search') {
      // Modo búsqueda - necesita artículo seleccionado o código
      if (!selectedExistingArticle && !newCode.trim()) {
        setRecodeError('Por favor seleccione un artículo existente o ingrese un código');
        return;
      }
    } else {
      // Modo manual - necesita código y que no exista
      if (!newCode.trim()) {
        setRecodeError('Por favor ingrese un código');
        return;
      }
      if (codeExists === true) {
        setRecodeError('Este código ya existe. Por favor ingrese un código diferente');
        return;
      }
    }
    
    setRecodeLoading(true);
    setRecodeError(null);
    
    try {
      const quantity = quantitiesToMove[selectedArticleForRecode.id] || selectedArticleForRecode.quantity || 0;
      
      let finalCode = newCode.trim();
      
      if (recodeMode === 'auto') {
        // Recodificación automática - el backend generará el código con folio SC
        const request = {
          recode_type: 'auto',
          quantity: quantity,
        };
        const response = await recodeProductV2(companyId, selectedArticleForRecode.id, request);
        // El backend debería retornar el nuevo código en la respuesta
        finalCode = (response as any)?.new_code || (response as any)?.code || 'SC' + (Date.now().toString().slice(-8));
      } else if (selectedExistingArticle) {
        // Asignar a artículo existente
        const productId = selectedExistingArticle.id; // En el maestro de artículos, el id es el product_id
        
        if (!productId) {
          throw new Error('No se pudo obtener el ID del producto seleccionado');
        }
        
        const request = {
          recode_type: 'new_or_existing',
          product_id: productId,
          quantity: quantity,
        };
        
        const response = await recodeProductV2(companyId, selectedArticleForRecode.id, request);
        // El backend puede retornar el código, si no, usar el del artículo seleccionado
        finalCode = (response as any)?.new_code || (response as any)?.code || selectedExistingArticle.code;
      } else {
        // Cambiar código del mismo artículo
        const request = {
          recode_type: 'change_code',
          new_code: newCode.trim(),
          quantity: quantity,
        };
        
        const response = await recodeProductV2(companyId, selectedArticleForRecode.id, request);
        // El backend puede retornar el código, si no, usar el ingresado
        finalCode = (response as any)?.new_code || (response as any)?.code || newCode.trim();
      }
      
      // Mostrar el código nuevo
      setNewCodeAfterRecode(finalCode);
      setRecodeDialogOpen(false);
      // Recargar la lista de artículos
      loadArticles(filters);
      
      // Mostrar mensaje de éxito con el código nuevo
      Swal.fire({
        title: '¡Recodificación exitosa!',
        html: `<p>El artículo ha sido recodificado exitosamente.</p><p><strong>Nuevo código: ${finalCode}</strong></p><p>Puede usar este código para reimprimir la etiqueta del producto.</p>`,
        icon: 'success',
        confirmButtonText: 'Aceptar',
        allowOutsideClick: false,
      }).then(() => {
        setNewCodeAfterRecode(null);
      });
      
    } catch (error: any) {
      console.error('❌ Error recoding product:', error);
      setRecodeError(error.message || 'Error al recodificar el producto');
    } finally {
      setRecodeLoading(false);
    }
  };

  const getTotalPages = () => {
    if (!pagination) return 1;
    return Math.ceil(pagination.total_elements / pagination.size);
  };

  const isAllSelected = Boolean(
    articles &&
      articles.length > 0 &&
      selectedArticles.length === articles.length
  );
  const isIndeterminate =
    selectedArticles.length > 0 &&
    selectedArticles.length < (articles?.length || 0);

  // Verificar si hay filtros activos (primarios o secundarios)
  const hasPrimaryFilter = filters.location_id || filters.container_id;
  const hasSecondaryFilters = filters.code || filters.description || filters.reference || filters.status || filters.provider_id || filters.origin_container_code;
  const hasAnyFilter = hasPrimaryFilter || hasSecondaryFilters;
  
  // Mostrar artículos si hay algún filtro activo (primario o secundario)
  if (!hasAnyFilter) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <Typography>
              Selecciona una ubicación, contenedor u otro filtro para ver los artículos disponibles
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!articles && (!pagination || pagination.total_elements === 0)) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <Typography>
              No hay artículos disponibles para reclasificar
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">
            Artículos disponibles para reclasificar (
            {pagination?.total_elements || 0})
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            {selectedArticles.length > 0 && (
              <Chip
                label={`${selectedArticles.length} seleccionados`}
                color="primary"
                variant="outlined"
                onDelete={clearSelection}
              />
            )}
            <IconButton
              color="primary"
              disabled={selectedArticles.length === 0}
              onClick={handleReclassifySelected}
              size="large"
            >
              <Tooltip title="Reclasificar seleccionados">
                <MoveIcon />
              </Tooltip>
            </IconButton>
          </Box>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    disabled={!articles || articles.length === 0}
                  />
                </TableCell>
                <TableCell>Artículo</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Cantidad Actual</TableCell>
                <TableCell>Cantidad a Mover</TableCell>
                <TableCell>Observación</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {articles?.map((article: ArticleForReclassify) => (
                <TableRow
                  key={article.id}
                  hover
                  selected={isArticleSelected(article.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isArticleSelected(article.id)}
                      onChange={() => handleArticleSelect(article.id)}
                    />
                  </TableCell>

                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        src={article.files?.[0]?.URL}
                        alt={article.name || article.description}
                        sx={{ width: 40, height: 40 }}
                      >
                        {(article.name || article.description).charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {article.name || article.description}
                        </Typography>
                        {article.description && article.name && (
                          <Typography variant="caption" color="text.secondary">
                            {article.description}
                          </Typography>
                        )}
                        {/* Mostrar ubicación o contenedor si el artículo fue seleccionado desde filtro (no desde ubicación/contenedor) */}
                        {(!filters.location_id && !filters.container_id) && (
                          <Box display="flex" gap={1} mt={0.5}>
                            {article.location_id && article.location_id > 0 && (article.location_name || article.location_description) ? (
                              <Chip
                                label={article.location_name || article.location_description}
                                size="small"
                                icon={<LocationIcon />}
                                variant="outlined"
                                sx={{ 
                                  height: 36, 
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  '& .MuiChip-icon': {
                                    fontSize: '1.2rem'
                                  }
                                }}
                              />
                            ) : null}
                            {article.container_id && article.container_id > 0 && article.container_code ? (
                              <Chip
                                label={article.container_code}
                                size="small"
                                icon={<ContainerIcon />}
                                variant="outlined"
                                sx={{ 
                                  height: 36, 
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  '& .MuiChip-icon': {
                                    fontSize: '1.2rem'
                                  }
                                }}
                              />
                            ) : null}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {article.code || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={articleStatuses[article.id] || article.status_id || ''}
                        displayEmpty
                        onChange={(e) => {
                          const statusId = parseInt(e.target.value as string) || 0;
                          handleStatusChange(article.id, statusId);
                        }}
                      >
                        <MenuItem value="">Sin estado</MenuItem>
                        {(reclassifyProductStates || []).map((state) => (
                          <MenuItem key={state.id} value={state.id}>
                            {state.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" fontWeight="medium" color="primary">
                      {article.quantity && article.quantity > 0 ? article.quantity : '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={quantitiesToMove[article.id] ?? (article.quantity && article.quantity > 0 ? article.quantity : '')}
                      onChange={(e) => {
                        const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                        handleQuantityChange(article.id, value);
                      }}
                      inputProps={{ 
                        min: 0, 
                        max: article.quantity || 0,
                        style: { textAlign: 'center' }
                      }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Observación..."
                      multiline
                      rows={1}
                      value={articleObservations[article.id] || ''}
                      onChange={(e) => {
                        handleObservationChange(article.id, e.target.value);
                      }}
                      sx={{ width: 120 }}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Box display="flex" gap={0.5}>
                    <IconButton
                      color="primary"
                        onClick={() => {
                          // Enriquecer el artículo con los cambios del usuario
                          const enrichedArticle = {
                            ...article,
                            status_id: articleStatuses[article.id] || article.status_id,
                            quantity: quantitiesToMove[article.id] || article.quantity,
                            observation: articleObservations[article.id] || ''
                          };
                          onOpenReclassifyDialog([enrichedArticle]);
                        }}
                      size="small"
                      disabled={selectedArticles.length > 0}
                    >
                      <Tooltip title={selectedArticles.length > 0 ? "Deshabilitado cuando hay artículos seleccionados. Use el botón superior para reclasificar múltiples." : "Reclasificar"}>
                        <MoveIcon />
                      </Tooltip>
                    </IconButton>
                      <IconButton
                        color="secondary"
                        onClick={() => handleRecodeClick(article)}
                        size="small"
                        disabled={selectedArticles.length > 1}
                      >
                        <Tooltip title={selectedArticles.length > 1 ? "Deshabilitado cuando hay múltiples seleccionados" : "Recodificar"}>
                          <RecodeIcon />
                        </Tooltip>
                      </IconButton>
                      <IconButton
                        color="info"
                        onClick={() => handleDetailedEditClick(article)}
                        size="small"
                        disabled={selectedArticles.length > 0}
                      >
                        <Tooltip title={selectedArticles.length > 0 ? "Deshabilitado cuando hay artículos seleccionados" : "Editar Detalles"}>
                          <EditIcon />
                        </Tooltip>
                      </IconButton>
                      <IconButton
                        color="secondary"
                        onClick={() => {
                          // Ir directamente al editor de artículos con el código
                          // Usar código en lugar de ID ya que la API solo permite buscar por código
                          if (article.code) {
                            window.open(`/dashboard/article/edit?code=${article.code}`, '_blank');
                          } else if (article.product_id) {
                            // Si no hay código, mostrar mensaje
                            Swal.fire({
                              title: 'Información',
                              text: 'Este artículo no tiene código. Por favor, búsquelo por código en el maestro de artículos.',
                              icon: 'info',
                              confirmButtonText: 'Aceptar',
                            });
                          }
                        }}
                        size="small"
                        disabled={selectedArticles.length > 0}
                      >
                        <Tooltip title={selectedArticles.length > 0 ? "Deshabilitado cuando hay artículos seleccionados" : "Editar en Maestro (Imágenes y Descripción)"}>
                          <ImageIcon />
                        </Tooltip>
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}

              {articlesLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <CircularProgress size={40} />
                      <Typography color="text.secondary" variant="h6">
                        Cargando artículos...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (!articles || articles.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <Typography color="text.secondary" variant="h6">
                        No se encontraron artículos para reclasificar
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        {pagination && pagination.total_elements > 0 && getTotalPages() > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <MuiPagination
              count={getTotalPages()}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </CardContent>

      {/* Diálogo de Recodificación */}
      <Dialog
        open={recodeDialogOpen}
        onClose={() => setRecodeDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <RecodeIcon color="primary" />
            <Typography variant="h6">Recodificar Artículo</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedArticleForRecode && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Artículo a recodificar:
                </Typography>
                <Box display="flex" alignItems="center" gap={2} p={2} bgcolor="grey.50" borderRadius={1}>
                  <Avatar src={selectedArticleForRecode.files?.[0]?.URL} sx={{ width: 40, height: 40 }}>
                    {selectedArticleForRecode.description.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedArticleForRecode.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Código actual: {selectedArticleForRecode.code}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Error message */}
              {recodeError && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {recodeError}
                  </Alert>
                </Grid>
              )}

              {/* Información básica del artículo */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                  Información Básica
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Cantidad a recodificar"
                      type="number"
                      value={quantitiesToMove[selectedArticleForRecode.id] || selectedArticleForRecode.quantity || 0}
                      onChange={(e) => handleQuantityChange(selectedArticleForRecode.id, parseInt(e.target.value) || 0)}
                      inputProps={{ 
                        min: 0, 
                        max: selectedArticleForRecode.quantity || 0 
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Nuevo código del artículo */}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Typography variant="subtitle2">
                    Nuevo Código del Artículo
                  </Typography>
                </Box>
                
                {/* Selector de modo de recodificación */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Modo de Recodificación</InputLabel>
                  <Select
                    value={recodeMode}
                    label="Modo de Recodificación"
                    onChange={(e) => {
                      const mode = e.target.value as 'manual' | 'search' | 'auto';
                      setRecodeMode(mode);
                      setIsSearchMode(mode === 'search');
                      setNewCode('');
                      setSelectedExistingArticle(null);
                      setCodeExists(null);
                    }}
                  >
                    <MenuItem value="auto">Automática (Folio SC)</MenuItem>
                    <MenuItem value="manual">Ingreso Manual</MenuItem>
                    <MenuItem value="search">Buscar Existente</MenuItem>
                  </Select>
                </FormControl>
                
                {recodeMode === 'auto' ? (
                  <Box p={2} bgcolor="info.50" borderRadius={1} border="1px solid" borderColor="info.main">
                    <Typography variant="body2" color="info.main">
                      ℹ️ Se generará automáticamente un nuevo código siguiendo el folio SC
                    </Typography>
                  </Box>
                ) : recodeMode === 'manual' ? (
                  <>
                    <TextField
                      fullWidth
                      label="Código del artículo"
                      value={newCode}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setNewCode(value);
                        // Validar código con debounce
                        if (value.trim().length >= 2) {
                          const timeout = setTimeout(() => {
                            validateCodeExists(value);
                          }, 500);
                          return () => clearTimeout(timeout);
                        } else {
                          setCodeExists(null);
                        }
                      }}
                      placeholder="Ingrese el nuevo código del artículo..."
                      error={codeExists === true}
                      helperText={
                        codeValidationLoading 
                          ? 'Validando...' 
                          : codeExists === true 
                            ? '⚠️ Este código ya existe' 
                            : codeExists === false 
                              ? '✅ Código disponible' 
                              : 'Ingrese el código a validar'
                      }
                      InputProps={{
                        startAdornment: <EditIcon color="action" sx={{ mr: 1 }} />,
                      }}
                    />
                  </>
                ) : (
                  <Autocomplete
                    freeSolo
                    options={Array.isArray(masterArticlesSearchResults) ? masterArticlesSearchResults : []}
                    getOptionLabel={(option: any) => {
                      if (typeof option === 'string') return option;
                      return `${option.code} - ${option.name || option.description || ''}`;
                    }}
                    value={selectedExistingArticle || newCode}
                    onInputChange={(event, newInputValue, reason) => {
                      setNewCode(newInputValue);
                      
                      if (reason === 'input' && newInputValue.length >= 2) {
                        handleSearchExisting(newInputValue);
                      } else if (reason === 'clear') {
                        setMasterArticlesSearchResults([]);
                      }
                    }}
                    onChange={(event, newValue, reason) => {
                      if (typeof newValue === 'string') {
                        setNewCode(newValue);
                        setSelectedExistingArticle(null);
                      } else if (newValue && typeof newValue === 'object') {
                        setNewCode(newValue.code);
                        setSelectedExistingArticle(newValue);
                      } else {
                        setNewCode('');
                        setSelectedExistingArticle(null);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar artículo existente (Maestro)"
                        placeholder="Escribe para buscar artículos del maestro..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                        }}
                      />
                    )}
                    renderOption={(props, option: any) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {option.code} - {option.name || option.description || ''}
                          </Typography>
                          {option.brand && (
                            <Typography variant="caption" color="text.secondary">
                              Marca: {option.brand}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    noOptionsText="No se encontraron artículos"
                    loading={masterArticlesSearchLoading}
                    loadingText="Buscando artículos..."
                  />
                )}
                
                {/* Mostrar información de la selección */}
                {selectedExistingArticle && (
                  <Box mt={2} p={2} bgcolor="success.50" borderRadius={1} border="1px solid" borderColor="success.main">
                    <Typography variant="body2" color="success.main">
                      ✅ Artículo existente seleccionado: <strong>{selectedExistingArticle.code}</strong> - {selectedExistingArticle.name || selectedExistingArticle.description}
                    </Typography>
                  </Box>
                )}
                
                {!selectedExistingArticle && newCode && recodeMode === 'manual' && (
                  <Box mt={2} p={2} bgcolor={codeExists === true ? "error.50" : "info.50"} borderRadius={1} border="1px solid" borderColor={codeExists === true ? "error.main" : "info.main"}>
                    <Typography variant="body2" color={codeExists === true ? "error.main" : "info.main"}>
                      {codeExists === true ? '⚠️' : '📝'} Código: <strong>{newCode}</strong>
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={() => setRecodeDialogOpen(false)}
            disabled={recodeLoading}
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleRecodeSubmit}
            variant="contained"
            disabled={(() => {
              const quantity = selectedArticleForRecode ? (quantitiesToMove[selectedArticleForRecode.id] || 0) : 0;
              
              // Si es modo automático, no necesita código
              if (recodeMode === 'auto') {
                return recodeLoading || !selectedArticleForRecode || quantity <= 0;
              }
              
              // Si es modo búsqueda, necesita artículo seleccionado o código
              if (recodeMode === 'search') {
                return recodeLoading || (!selectedExistingArticle && !newCode.trim()) || !selectedArticleForRecode || quantity <= 0;
              }
              
              // Modo manual: necesita código y que no exista (o que el usuario confirme)
              const hasNewCode = !!newCode.trim();
              return recodeLoading || !hasNewCode || !selectedArticleForRecode || quantity <= 0 || codeExists === true;
            })()}
            startIcon={recodeLoading ? <CircularProgress size={20} /> : <RecodeIcon />}
            sx={{ borderRadius: 2 }}
          >
            {recodeLoading ? 'Recodificando...' : 'Recodificar Artículo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edición Detallada */}
      <Dialog
        open={detailedEditDialogOpen}
        onClose={() => setDetailedEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            minHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon color="primary" />
            <Typography variant="h6">Editar Detalles del Artículo</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedArticleForDetailedEdit && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Información del artículo */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Artículo:
                </Typography>
                <Box display="flex" alignItems="center" gap={2} p={2} bgcolor="grey.50" borderRadius={1}>
                  <Avatar src={selectedArticleForDetailedEdit.files?.[0]?.URL} sx={{ width: 40, height: 40 }}>
                    {selectedArticleForDetailedEdit.description.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedArticleForDetailedEdit.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Código: {selectedArticleForDetailedEdit.code}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Error message */}
              {detailedEditError && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {detailedEditError}
                  </Alert>
                </Grid>
              )}

              {/* Estados del Producto */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Estados del Producto
                </Typography>
                
                <Grid container spacing={2}>
                  {/* Uso */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Uso *</InputLabel>
                      <Select
                        value={detailedForm.usage_id || ''}
                        label="Uso *"
                        onChange={(e) => setDetailedForm(prev => ({ ...prev, usage_id: parseInt(e.target.value as string) || 0 }))}
                        disabled={false}
                      >
                        {(productUsages || []).map((usage: any) => (
                          <MenuItem key={usage.id} value={usage.id}>
                            {usage.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observación Uso"
                      value={detailedForm.usage_observation}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, usage_observation: e.target.value }))}
                      placeholder="Observación sobre el uso..."
                    />
                  </Grid>

                  {/* Empaquetado */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Empaquetado *</InputLabel>
                      <Select
                        value={detailedForm.packaging_id || ''}
                        label="Empaquetado *"
                        onChange={(e) => setDetailedForm(prev => ({ ...prev, packaging_id: parseInt(e.target.value as string) || 0 }))}
                        disabled={false}
                      >
                        {(productPackagings || []).map((packaging: any) => (
                          <MenuItem key={packaging.id} value={packaging.id}>
                            {packaging.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observación Empaquetado"
                      value={detailedForm.packaging_observation}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, packaging_observation: e.target.value }))}
                      placeholder="Observación sobre el empaquetado..."
                    />
                  </Grid>

                  {/* Funcionalidad */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Funcionalidad *</InputLabel>
                      <Select
                        value={detailedForm.functionality_id || ''}
                        label="Funcionalidad *"
                        onChange={(e) => setDetailedForm(prev => ({ ...prev, functionality_id: parseInt(e.target.value as string) || 0 }))}
                        disabled={false}
                      >
                        {(productFuntionalities || []).map((functionality: any) => (
                          <MenuItem key={functionality.id} value={functionality.id}>
                            {functionality.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observación Funcionalidad"
                      value={detailedForm.functionality_observation}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, functionality_observation: e.target.value }))}
                      placeholder="Observación sobre la funcionalidad..."
                    />
                  </Grid>

                  {/* Estético */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Estético *</InputLabel>
                      <Select
                        value={detailedForm.aesthetic_id || ''}
                        label="Estético *"
                        onChange={(e) => setDetailedForm(prev => ({ ...prev, aesthetic_id: parseInt(e.target.value as string) || 0 }))}
                        disabled={false}
                      >
                        {(productAesthetics || []).map((aesthetic: any) => (
                          <MenuItem key={aesthetic.id} value={aesthetic.id}>
                            {aesthetic.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observación Estético"
                      value={detailedForm.aesthetic_observation}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, aesthetic_observation: e.target.value }))}
                      placeholder="Observación sobre el estado estético..."
                    />
                  </Grid>

                  {/* Completitud */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Completitud *</InputLabel>
                      <Select
                        value={detailedForm.completeness_id || ''}
                        label="Completitud *"
                        onChange={(e) => setDetailedForm(prev => ({ ...prev, completeness_id: parseInt(e.target.value as string) || 0 }))}
                        disabled={false}
                      >
                        {(productCompletenesses || []).map((completeness: any) => (
                          <MenuItem key={completeness.id} value={completeness.id}>
                            {completeness.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observación Completitud"
                      value={detailedForm.completeness_observation}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, completeness_observation: e.target.value }))}
                      placeholder="Observación sobre la completitud..."
                    />
                  </Grid>

                  {/* Estado */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Estado *</InputLabel>
                      <Select
                        value={detailedForm.state_id || ''}
                        label="Estado *"
                        onChange={(e) => setDetailedForm(prev => ({ ...prev, state_id: parseInt(e.target.value as string) || 0 }))}
                        disabled={false}
                      >
                        {(masterProductStates || []).map((state: any) => (
                          <MenuItem key={state.id} value={state.id}>
                            {state.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              {/* Estado General */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Estado General22
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Referencia"
                      value={detailedForm.reference}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, reference: e.target.value }))}
                      placeholder="Referencia del producto..."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observación General"
                      value={detailedForm.observation}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, observation: e.target.value }))}
                      placeholder="Observación general..."
                    />
                  </Grid>
                </Grid>
              </Grid>

            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={() => setDetailedEditDialogOpen(false)}
            disabled={detailedEditLoading}
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveDetailedEdit}
            variant="contained"
            disabled={detailedEditLoading}
            startIcon={detailedEditLoading ? <CircularProgress size={20} /> : <EditIcon />}
            sx={{ borderRadius: 2 }}
          >
            {detailedEditLoading ? 'Guardando...' : 'Guardar Detalles'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
